import React, { useMemo } from "react";
import t from "../../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { useQuantities } from "../../../../../api/useQuantities";
import DropzoneV2 from "@ui/DropzoneV2";
import { parseDisplayFilterFile } from "../../../../../domain/parser/quantity/displayFilter/csv";
import { isResultErr } from "../../../../../lib/patterns/result";
import {
  type DisplayFilterParsingError,
  type DisplayFilterParsingResult,
} from "../../../../../domain/parser/quantity/displayFilter/csv/index";
import { getDisplayFilterParsingErrorMessage } from "../../../../../domain/parser/quantity/displayFilter/csv/errors";
import { usePost } from "../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { useNavigate } from "react-router-dom";
import { toPath } from "../../../../../router/helpers/path";
import { css } from "@linaria/core";
import { useDisplayFilterPreferences } from "../../../EvolutionSubHeader/useDisplayFilterPreferences";

interface P {
  onRequestClose: () => void;
  projectId: string;
  needRedirect?: boolean;
}

const DisplayFiltersModalForm = ({ onRequestClose, projectId, needRedirect = false }: P) => {
  const quantities = useQuantities(projectId);
  const replaceDisplayFilters = usePost("/projects/:projectId(uuid)/displayFilters");

  const { addToast } = useToasts();
  const navigate = useNavigate();

  const { setDisplayFilterPreferences } = useDisplayFilterPreferences(projectId);

  const [parsingResult, setParsingResult] = React.useState<DisplayFilterParsingResult>();
  const [filename, setFilename] = React.useState<string>("");
  const [isValid, setIsValid] = React.useState<boolean>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<DisplayFilterParsingError[]>();
  const isDisplayFilterValid = isValid && (parsingResult?.data?.length || 0) > 0;

  const handleSubmit = useMemo(
    () => async () => {
      if (parsingResult) {
        try {
          await replaceDisplayFilters({
            body: { fileName: filename, filters: parsingResult.data },
            routeParams: { projectId },
          });
          setDisplayFilterPreferences([]);
          addToast(
            "success",
            t("project.filters.modal.import.toast.success", {
              count: parsingResult.data.length,
            }),
          );
          onRequestClose();
          if (needRedirect) {
            void navigate(toPath("/projects/:projectId/settings/display-filters", { projectId }));
          }
        } catch (error) {
          addToast("error", t("errors.backend_error"));
          return false;
        }
      }
      return true;
    },
    [
      addToast,
      filename,
      navigate,
      onRequestClose,
      parsingResult,
      projectId,
      replaceDisplayFilters,
      setDisplayFilterPreferences,
      needRedirect,
    ],
  );

  const handleDrop = React.useCallback(
    async ([file]: File[]) => {
      if (!file) return;

      setFilename(file.name);

      const result = await parseDisplayFilterFile(file, {
        expectedQuantityNameValues: quantities.map(({ id, name, formula }) => ({
          id,
          name,
          formula,
        })),
      });

      if (isResultErr(result) && result.value[0]) {
        setIsValid(false);
        setErrorText(getDisplayFilterParsingErrorMessage(result.value[0]));
        setErrorDetails(result.value);
        setParsingResult(undefined);
        return;
      }

      setIsValid(true);
      setParsingResult(result.value as DisplayFilterParsingResult);
    },
    [quantities],
  );

  const downloadErrors = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_display_filter_errors.json`);
    a.click();
  }, [errorDetails]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={isDisplayFilterValid ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
    >
      <div className={DropzoneContainer}>
        <DropzoneV2
          onDrop={handleDrop}
          accept=".csv"
          nameFile={filename}
          version="lithium"
          pathSampleFile="/metroscopia_example_display_filters.csv"
          multiple={false}
          validation={isValid}
          errorText={isValid ? undefined : errorText}
          downloadErrors={downloadErrors}
        />
      </div>
    </ModalForm>
  );
};

export default DisplayFiltersModalForm;

//#region CSS
const DropzoneContainer = css`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
//#endregion CSS
